<template>
  <div class="spreadsTable margin-general">
    <h2 class="spreadsTable-title">{{ $t("spreadsTable.titleTable") }}</h2>
    <p class="spreadsTable-description">{{ $t("spreadsTable.descTable") }}</p>
    <p class="spreadsTable-note">{{ $t("spreadsTable.indicativeNote") }}</p>

    <SelectCustom
      class="selectItem"
      :options="tabs"
      v-model="selected"
    />

    <div class="buttonsContainer" ref="buttonsContainer">
      <button
        v-for="tab in tabs"
        :key="tab.name"
        class="buttonItem"
        :class="{ selected: selected === tab.value }"
        @click="selectTab(tab.value)"
      >
        {{ $t(tab.label) }}
      </button>

      <span class="selected-border" :style="borderStyle">
        <div class="border-style"></div>
      </span>
    </div>

    <div class="card-content">
      <div
        v-for="row in categories[selected]"
        :key="row.symboltext"
        class="card"
        :class="{ up: row.trend === 'up', down: row.trend === 'down' }"
      >
        <div class="content line-gray">
          <p class="name-spread color-spread">{{ getCardName(row, $t) }}</p>
          <span
            class="material-symbols-outlined name-spread"
            translate="no"
            v-if="row.trend === 'up'"
            >arrow_upward_alt</span
          >
          <span
            class="material-symbols-outlined name-spread"
            translate="no"
            v-if="row.trend === 'down'"
            >arrow_downward_alt</span
          >
        </div>
        <div class="content">
          <div>
            <span class="name-column">{{ $t("spreadsTable.bid") }}</span>
            <p class="bid-ask color-spread">
              {{ row.values[1][0] }}
              {{ row.values[1][1] }}
              {{ row.values[1][2] }}
              {{ row.values[1][3] }}
              {{ row.values[1][4] }}
              {{ row.values[1][5] }}
              {{ row.values[1][6] }}
            </p>
          </div>
          <div class="column-right">
            <span class="name-column">{{ $t("spreadsTable.ask") }}</span>
            <p class="bid-ask color-spread">
              {{ row.values[2][0] }}
              {{ row.values[2][1] }}
              {{ row.values[2][2] }}
              {{ row.values[2][3] }}
              {{ row.values[2][4] }}
              {{ row.values[2][5] }}
              {{ row.values[2][6] }}
            </p>
          </div>
        </div>
        <div class="content">
          <div>
            <span class="name-column">{{ $t("spreadsTable.spreads") }}</span>
            <p class="spreads-text">{{ row.values[3] }}</p>
            <span class="spread-context">{{ $t("spreadsTable.spreadContext") }}</span>
          </div>
          <!-- <div class="column-right">
            <span class="name-column">{{ $t("spreadsTable.leverage") }}</span>
            <p class="leverage">{{ $t(`spreadsTable.leverageNo.${selected}`) }}</p>
          </div> -->
        </div>
      </div>
    </div>

    <swiper
      :slidesPerView="2"
      :spaceBetween="20"
      :loop="true"
      :pagination="{
        clickable: true,
      }"
      :modules="modules"
      class="mySwiper"
      :breakpoints="{
        '500': {
          slidesPerView: 2,
          spaceBetween: 20,
          slidesPerGroup: 1,
        },
        '0': {
          slidesPerView: 1,
          spaceBetween: 0,
          slidesPerGroup: 1,
        },
      }"
    >
      <swiper-slide
        v-for="row in categories[selected]"
        :key="row.symbol"
        class="card"
        :class="{ up: row.trend === 'up', down: row.trend === 'down' }"
      >
        <div class="content line-gray">
          <p class="name-spread color-spread">{{ getCardName(row, $t) }}</p>
          <span
            class="material-symbols-outlined name-spread"
            translate="no"
            v-if="row.trend === 'up'"
            >arrow_upward_alt</span
          >
          <span
            class="material-symbols-outlined name-spread"
            translate="no"
            v-if="row.trend === 'down'"
            >arrow_downward_alt</span
          >
        </div>
        <div class="content">
          <div>
            <span class="name-column">{{ $t("spreadsTable.bid") }}</span>
            <p class="bid-ask color-spread">
              {{ row.values[1][0] }}
              {{ row.values[1][1] }}
              {{ row.values[1][2] }}
              {{ row.values[1][3] }}
              {{ row.values[1][4] }}
              {{ row.values[1][5] }}
              {{ row.values[1][6] }}
            </p>
          </div>
          <div class="column-right">
            <span class="name-column">{{ $t("spreadsTable.ask") }}</span>
            <p class="bid-ask color-spread">
              {{ row.values[2][0] }}
              {{ row.values[2][1] }}
              {{ row.values[2][2] }}
              {{ row.values[2][3] }}
              {{ row.values[2][4] }}
              {{ row.values[2][5] }}
              {{ row.values[2][6] }}
            </p>
          </div>
        </div>
        <div class="content">
          <div>
            <span class="name-column">{{ $t("spreadsTable.spreads") }}</span>
            <p class="spreads-text">{{ row.values[3] }}</p>
            <span class="spread-context">{{ $t("spreadsTable.spreadContext") }}</span>
          </div>
          <!-- <div class="column-right">
            <span class="name-column">{{ $t("spreadsTable.leverage") }}</span>
            <p class="leverage">{{ $t(`spreadsTable.leverageNo.${selected}`) }}</p>
          </div> -->
        </div>
      </swiper-slide>
    </swiper>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination, Autoplay } from "swiper/modules";

import SelectCustom from "../utils/select/SelectCustom.vue";

const selected = ref("currencies");
const selectedButtonIndex = ref(0);
const borderStyle = ref({});
const tabs = ref([
  { value: "currencies", label: "spreadsTable.categories1" },
  { value: "indices", label: "spreadsTable.categories2" },
  { value: "cryptos", label: "spreadsTable.categories3" },
  { value: "commodities", label: "spreadsTable.categories4" },
]);
const leverageTexts = {
  currencies: "spreadsTable.leverageNo",
  cryptos: "spreadsTable.leverageNo",
  indices: "spreadsTable.leverageNo",
  commodities: "spreadsTable.leverageNo",
};

const leverageText = ref("");

watch(selected, () => {
  leverageText.value = leverageTexts[selected.value];
});

const categories = ref({
  currencies: [],
  cryptos: [],
  indices: [],
  commodities: [],
});
const sequences = ref(null);
const modules = ref([Pagination, Autoplay]);

const getCardName = (row, translate) => {
  const categoryTranslations = {
    currencies: [
      "spreadsTable.EURUSD",
      "spreadsTable.GBPUSD",
      "spreadsTable.USDJPY",
      "spreadsTable.USDCAD",
      "spreadsTable.AUDUSD",
      "spreadsTable.XAUUSD",
    ],
    indices: [
      "spreadsTable.FCHI",
      "spreadsTable.WS30",
      "spreadsTable.NDX",
      "spreadsTable.UK100",
    ],
    cryptos: [
      "spreadsTable.BTCUSD",
      "spreadsTable.BTCJPY",
      "spreadsTable.ETHUSD",
      "spreadsTable.LTDUSD",
    ],
    commodities: [
      "spreadsTable.XAGUSD",
      "spreadsTable.CRUDEOIL",
      "spreadsTable.XAUEUR",
    ],
  };

  const category = selected.value;
  const cardIndex = categories.value[category].findIndex((c) => c === row);

  if (categoryTranslations[category] && cardIndex !== -1) {
    const symbolText = categoryTranslations[category][cardIndex];
    return translate(symbolText);
  }

  return ""; // Puedes ajustar esto según tus necesidades
};

const fetchData = async () => { 
  try {
    const response = await fetch(
      "https://y1agldo4ad.execute-api.us-west-2.amazonaws.com/Prod/spreads/EURUSD%7CGBPUSD%7CUSDJPY%7CUSDCAD%7CAUDUSD%7CBTCUSD%7CBTCJPY%7CETHUSD%7CLTCUSD%7CFCHI%7CNDX%7CWS30%7CUK100%7CXAUUSD%7CXAGUSD%7CCRUDEOIL%7CXAUEUR"
    );

    if (response.ok) {
      const data = await response.json();
      const spreads = data.result.data;
      const _rows = {
        currencies: [],
        cryptos: [],
        indices: [],
        commodities: [],
      };
      spreads.forEach((row) => {
        const values = {
          symbol: row.symbol,
          trend: row.trend,
          values: [row.symbol, row.bid, row.ask, row.spread],
        };
        switch (row.symbol) {
          case "EURUSD":
            _rows.currencies.push(values);
            break;
          case "GBPUSD":
            _rows.currencies.push(values);
            break;
          case "USDJPY":
            _rows.currencies.push(values);
            break;
          case "USDCAD":
            _rows.currencies.push(values);
            break;
          case "AUDUSD":
            _rows.currencies.push(values);
            break;
          case "XAUUSD":
            _rows.currencies.push(values);
            break;
          case "BTCUSD":
            _rows.cryptos.push(values);
            break;
          case "BTCJPY":
            _rows.cryptos.push(values);
            break;
          case "ETHUSD":
            _rows.cryptos.push(values);
            break;
          case "LTCUSD":
            _rows.cryptos.push(values);
            break;

          case "NDX":
            _rows.indices.push(values);
            break;
          case "UK100":
            _rows.indices.push(values);
            break;
          case "FCHI":
            _rows.indices.push(values);
            break;
          case "WS30":
            _rows.indices.push(values);
            break;

          case "XAGUSD":
            _rows.commodities.push(values);
            break;
          case "CRUDEOIL":
            _rows.commodities.push(values);
            break;
          case "XAUEUR":
            _rows.commodities.push(values);
            break;
        }
      });
      if (sequences.value === null) {
        initializeSequences(_rows, "currencies", 6, [
          "EURUSD",
          "GBPUSD",
          "USDJPY",
          "USDCAD",
          "AUDUSD",
          "XAUUSD",
        ]);
        initializeSequences(_rows, "cryptos", 6, [
          "BTCUSD",
          "BTCJPY",
          "ETHUSD",
          "LTCUSD",
        ]);
        initializeSequences(_rows, "indices", 6, [
          "UKX",
          "FCHI",
          "WS30",
          "NDX",
        ]);
        initializeSequences(_rows, "commodities", 4, [
          "XAGUSD",
          "CRUDEOIL",
          "XAUEUR",
        ]);
      }
      categories.value = {
        currencies: getNextSequence(_rows, "currencies"),
        cryptos: getNextSequence(_rows, "cryptos"),
        indices: getNextSequence(_rows, "indices"),
        commodities: getNextSequence(_rows, "commodities"),
      };
    } else {
      console.error("Error al obtener los datos");
    }
  } catch (error) {
    console.error("Error al realizar la solicitud:", error);
  }
};

const initializeSequences = (categories, value, len, priority) => {
  if (priority === null) {
    priority = [];
  }
  const symbols = pickRandom(categories[value], categories[value].length);
  if (sequences.value === null) {
    sequences.value = {};
  }
  const sequenceSymbols = [];
  const pending = symbols.map((item) => item.symbol);
  while (pending.length > 0) {
    const batchSymbols = [];
    while (priority.length > 0) {
      const prioritySymbol = priority.shift();
      for (let i = 0; i < pending.length; i++) {
        if (pending[i] === prioritySymbol) {
          batchSymbols.push(pending[i]);
          pending.splice(i, 1);
          break;
        }
      }
    }
    const l = len - batchSymbols.length;
    for (let i = 0; i < l; i++) {
      if (pending.length > 0) {
        batchSymbols.push(pending.pop());
      }
    }
    sequenceSymbols.push(batchSymbols);
  }
  sequences.value[value] = {
    current: 0,
    length: len,
    symbols: sequenceSymbols,
  };
};

const getNextSequence = (categories, value) => {
  const sequence = sequences.value[value];
  if (sequence !== null) {
    let current = sequence.current;
    const next = [];
    sequence.symbols[current].forEach((symbol) => {
      categories[value].forEach((el) => {
        if (el.symbol === symbol) {
          next.push(el);
        }
      });
    });
    current++;
    current %= sequence.symbols.length;
    sequence.current = current;
    return next;
  } else {
    return [];
  }
};

const pickRandom = (arr, count) => {
  const _arr = [...arr];
  return [...Array(count)].map(
    () => _arr.splice(Math.floor(Math.random() * _arr.length), 1)[0]
  );
};

const selectTab = (tabName) => {
  const newIndex = tabs.value.findIndex((tab) => tab.value === tabName);
  if (newIndex !== -1) {
    selected.value = tabName;
  }
  selectedButtonIndex.value = newIndex;
  updateBorderBottomStyle();
};

let lastBorderStyle = { width: null, left: null };
let borderDebounceTimeout = null;

const initializeBorderBottomStyle = () => {
  if (borderDebounceTimeout) clearTimeout(borderDebounceTimeout);
  borderDebounceTimeout = setTimeout(() => {
    const buttonsContainer = document.querySelector(".buttonsContainer");
    if (
      buttonsContainer &&
      buttonsContainer.children[selectedButtonIndex.value]
    ) {
      const button = buttonsContainer.children[selectedButtonIndex.value];
      // Lee todas las propiedades primero
      const width = button.offsetWidth;
      const left = button.offsetLeft;
      // Solo actualiza si los valores realmente cambian
      if (lastBorderStyle.width !== width || lastBorderStyle.left !== left) {
        lastBorderStyle = { width, left };
        borderStyle.value = {
          width: `${width}px`,
          left: `${left}px`,
        };
      }
    }
  }, 50); // Debounce de 50ms
};

const updateBorderBottomStyle = () => {
  if (borderDebounceTimeout) clearTimeout(borderDebounceTimeout);
  borderDebounceTimeout = setTimeout(() => {
    const button =
      document.querySelector(".buttonsContainer").children[
        selectedButtonIndex.value
      ];
    if (button) {
      // Lee todas las propiedades primero
      const width = button.offsetWidth;
      const left = button.offsetLeft;
      // Solo actualiza si los valores realmente cambian
      if (lastBorderStyle.width !== width || lastBorderStyle.left !== left) {
        lastBorderStyle = { width, left };
        borderStyle.value = {
          width: `${width}px`,
          left: `${left}px`,
        };
      }
    }
  }, 50); // Debounce de 50ms
};

let intervalId;
onMounted(async () => {
  await nextTick();
  initializeBorderBottomStyle();
  selected.value = "currencies";
  fetchData();
  if (process.client) {
    intervalId = setInterval(() => {
      fetchData();
    }, 2000);
  }
});

onBeforeUnmount(() => {
  if (process.client) {
    clearInterval(intervalId);
  }
});
</script>
